<script lang="typescript">
	import { genStaticPuzzle, PuzzleAttempt, locationEqual} from './puzzle'
	import Menu from './components/Menu.svelte'
	import MenuItem from './components/MenuItem.svelte'
	import { HelpCircleIcon, SkipBackIcon, SearchIcon, GridIcon, ListIcon, MusicIcon, SpeakerIcon, ImageIcon } from 'svelte-feather-icons'
	import type { Location } from './puzzle'
	import { solvePuzzle, genPuzzle } from './background-solver'
	import { fade } from 'svelte/transition'
	import { onMount, tick } from 'svelte'
	import Board from './Board.svelte'
	import NewGamePopup from './NewGamePopup.svelte'
	import HowToPlay from './HowToPlay.svelte'
	import { loadGame, saveCurrentGame, prefersMusic, setPrefersMusic, setPrefersSound, prefersSound, preferredTheme, setPreferredTheme } from './profile'
	import Credits from './Credits.svelte'
	import { playClick, stopMusic, startMusic, changeGenre } from './audio'
	import ThemeSelector from "./ThemeSelector.svelte";

	let width=4
	let height=4	
	let difficulty=1

	if (document.location) {
		let params = new URL(document.location.href).searchParams;
		if (params.get("difficulty")) {
			difficulty = Math.max(Math.min(Number(params.get("difficulty")) || difficulty,3),1);
		}
		if (params.get("size")) {
			width = Math.max(Math.min(Number(params.get("size")) || width,15), 2);
			height = width
		}
	}

	let theme = "default"

	let show_new_game = false;
	let show_menu = false;
	let show_how_to_play = false;
	let show_welcome = false;
	let show_credits = false;
	let show_theme_selector = false;

	let music = true;
	let sound = true;

	let attempt: PuzzleAttempt = null;
	let hint: { text: string, location: Location} = null;
	
	attempt = loadGame()
	
	if (!attempt) {
		show_welcome = true;
		newPuzzle(difficulty, width);
	} else {
		width = attempt.puzzle.width;
		difficulty = 2;
	}

	onMount(() => {
		music = prefersMusic()
		sound = prefersSound()
		theme = preferredTheme();
		if (music) {
			startMusic(theme == "synthwave" ? "synthwave" : "ambient");
		}
	})

	async function newPuzzle(new_difficulty, new_size) {
		difficulty = new_difficulty;
		hint = null;
		width = new_size;
		height = new_size;
		attempt = null;
		try {
			let puzzle = await genPuzzle(width, height, difficulty);
			attempt = new PuzzleAttempt(puzzle);
			console.log("got puzzle", attempt);
		} catch (e) {
			console.log("Error generating puzzle", e);
		}
	}
	
	async function solve(numSteps: number = null) {
		let res = await solvePuzzle(attempt, numSteps);
		attempt = res.solution;
	}


	async function showHint() {
		let res = await solvePuzzle(attempt, 1);
		if (!res.solvable) {
			//we need to undo a mistake
			let res = await solvePuzzle(new PuzzleAttempt(attempt.puzzle));
			let hintLocation = [...attempt.eachSlantLocation()].find(s => attempt.getSlant(s).value != 0 && attempt.getSlant(s).value != res.solution.getSlant(s).value);
			hint = {
				text: "This is invalid",
				location: hintLocation
			}
		} else {
			let hintLocation = [...attempt.eachSlantLocation()].find(s => attempt.getSlant(s).value == 0 && res.solution.getSlant(s).value != 0);
			hint = {
				text: res.stats.obviousSteps ? "Check the surrounding clues" :  (res.stats.lastValidChoiceStep ? "There is only one valid solution to this square" : "Examine the possible values for this and surrounding squares. This square will have the same value in all valid combinations."),
				location: hintLocation
			}
		}
	}

	async function undoMistakes() {
		let currentAttempt = attempt;
		let res = await solvePuzzle(new PuzzleAttempt(currentAttempt.puzzle));
		for (let s of attempt.eachSlantLocation()) {
			let currentValue = currentAttempt.getSlant(s).value;
			if (currentValue == 0) continue;
			let correctValue = res.solution.getSlant(s).value;
			if (currentValue != correctValue) {
				currentAttempt.setSlant(s, 0);
				// if we are updating the current puzzle
				if (currentAttempt == attempt) {
					attempt = attempt;
					await new Promise(resolve => setTimeout(resolve, 200));
				} else { 
					break;
				}
			}
		}
	}

	function squareClick(e: CustomEvent<{location: Location}>) {
		let location = e.detail.location;
		attempt.toggleSlant(location);
		attempt = attempt;
		if (hint && locationEqual(location, hint.location)) {
			hint = null;
		}
		if (sound) {
			playClick();
		}
	}

	function onNewPuzzle(e: Event) {
		newPuzzle((e as CustomEvent).detail.difficulty, (e as CustomEvent).detail.size)
	}

	function onThemeChange(e: Event) {
		let theme_id = (e as CustomEvent).detail.theme;
		theme = theme_id;
		setPreferredTheme(theme);
	}

	function toggleMusic() {
		if (music) {
			music = false;
			stopMusic();
		} else {
			music = true;
			startMusic();
		}
		setPrefersMusic(music);
	}

	function toggleSound() {
		sound = !sound;
		setPrefersSound(sound);
	}

	$: attempt != null && saveCurrentGame(attempt)

</script>

<div class="root {theme}">
	<div class="title-section">
		<span class="title">ななめ - Naname</span>
		<span class="menu-button" on:click={() => show_menu = !show_menu}>&#x2630;</span>
	</div>

	<main>
		{#if attempt}
			<Board attempt={attempt} highlight={hint ? hint.location : null} on:squareClick={(e) => squareClick(e)} />
		{:else}
			<div class="generating" in:fade={{delay:500}} out:fade>
				<h3>Generating Puzzle...</h3>
			</div>
		{/if}


		{#if show_new_game}
			<NewGamePopup new_difficulty={difficulty} new_size={width} on:close={() => show_new_game = false } on:newPuzzle={(e) => onNewPuzzle(e) }  />
		{/if}
		{#if show_menu}
			<Menu on:close={() => show_menu = false}>
				<MenuItem on:select={() => show_new_game = true} ><GridIcon size="20"/> New game</MenuItem>
				<MenuItem on:select={() => show_how_to_play = true} ><HelpCircleIcon size="20"/> How to play</MenuItem>
				{#if attempt}
					<MenuItem on:select={() => undoMistakes()}><SkipBackIcon size="20"/> Undo Errors</MenuItem>
					<MenuItem on:select={() => showHint()}><SearchIcon size="20"/> Hint</MenuItem>
				{/if}
				<MenuItem on:select={() => show_theme_selector = true}><ImageIcon size="20" /> Change Theme</MenuItem>
				<MenuItem on:select={() => toggleMusic()}><MusicIcon size="20" /> { music ? "Disable Music" : "Enable Music" }</MenuItem>
				<MenuItem on:select={() => toggleSound()}><SpeakerIcon size="20" /> { sound ? "Disable Sound Effects" : "Enable Sound Effects" }</MenuItem>
				<MenuItem on:select={() => show_credits = true}><ListIcon size="20" />  Credits</MenuItem>
			</Menu>
		{/if}

		{#if show_how_to_play}
			<HowToPlay step={1} on:close={() => show_how_to_play = false} />
		{/if}

		{#if show_welcome}
			<HowToPlay step={0} on:close={() => show_welcome = false} />
		{/if}

		{#if show_credits}
			<Credits on:close={() => show_credits = false} />
		{/if}

		{#if show_theme_selector}
				<ThemeSelector active_theme={theme} on:close={() => {
					show_theme_selector = false;
					changeGenre(theme == "synthwave" ? "synthwave": "ambient")
				}} on:theme={onThemeChange}  />
		{/if}

		{#if attempt && attempt.isComplete() && attempt.isValid() }
				<div class="success" transition:fade>
					<h1>Level Complete!</h1>
					<button on:click={() => show_new_game = true }>Play Again</button>
				</div>
		{/if}

		{#if hint}
			<div class="hint" on:click={() => hint = null}>
				{hint.text}
			</div>
		{/if}
	</main>
</div>
<style>
	.title-section {
		flex: 0;
		font-size: 1.25rem;
		padding: 0 1rem;
		line-height: 3rem;
		max-width: 100%;
		box-sizing: border-box;
		box-shadow: var(--shadow);
		background: var(--title-background);
		user-select: none;
	}

	main {
		flex: 1;
		display:flex;
		flex-direction: column;
		justify-content: center;
		overflow: auto;
	}

	.root {
		display: flex;
		flex-direction: column;
		height: 100vh;
		width: 100%;
		position: fixed;
	}

	.menu-button {
		float: right;
		margin-right: 0.5rem;
		text-transform: uppercase;
		cursor: pointer;
	}

	:global(button) {
		border: none;
		background-color: var(--button-color);
		color: var(--button-text-color);
		padding: 0.5rem 1rem;
	}

	
	.success {
		position: absolute;
		top: 25%;
		left: 0;
		right: 0;
		background: var(--panel-background);
		text-align: center;		
		padding: 0.2rem 0.5rem 1rem 0.5rem;
		box-shadow: var(--shadow);
	}

	.success h1 {
		font-size: 2.5rem;
	}

	.generating {
		position: absolute;
    	left: 50%;
		transform: translate(-50%, 0px);
	}

	.generating h3 {
		font-size: 1.5rem;
    	box-shadow: var(--shadow);
    	padding: 1.5rem 3rem;
    	background: var(--panel-background);
    	font-weight: normal;
	}

	.hint {
		position: fixed;
		bottom: 0;
		left: 0;
		box-shadow: var(--shadow);
		background-color: var(--button-color);
		color: var(--button-text-color);
		padding: 0.75rem;
		font-size: 1rem;
		max-width: 320px;
		margin: 0.5rem;
	}
</style>